// ===== BEGIN FILE: src/components/report-v2/sections/LifeAreasSection.tsx =====
/**
 * Life areas section with dashboard-style visualization
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// Per-rating color theme (hex values for inline styles — avoids Joy token lookup in sx)
const COLOR_THEMES = {
  success: { bg: '#f0fdf4', border: '#16a34a', accent: '#16a34a', barBg: '#bbf7d0', light: '#dcfce7', text: '#15803d', dosBg: '#f0fdf4' },
  primary: { bg: '#eff6ff', border: '#2563eb', accent: '#2563eb', barBg: '#bfdbfe', light: '#dbeafe', text: '#1d4ed8', dosBg: '#eff6ff' },
  warning: { bg: '#fffbeb', border: '#d97706', accent: '#d97706', barBg: '#fde68a', light: '#fef3c7', text: '#b45309', dosBg: '#fffbeb' },
  danger:  { bg: '#fef2f2', border: '#dc2626', accent: '#dc2626', barBg: '#fecaca', light: '#fee2e2', text: '#b91c1c', dosBg: '#fef2f2' },
} as const;

export const LifeAreasSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { lifeAreas } = data;

  const lifeAreaIcons: Record<string, string> = {
    health: '❤️', wealth: '💰', job: '💼', business: '📈',
    marriage: '💑', children: '👶', career: '💼', education: '📚',
    spirituality: '🕉️', property: '🏠', travel: '🌍', legal: '⚖️', creativity: '🎨',
  };

  const getRatingKey = (score: number): keyof typeof COLOR_THEMES => {
    if (score >= 75) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 30) return 'warning';
    return 'danger';
  };

  const getRatingLabel = (score: number): string => {
    if (score >= 75) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 30) return 'Fair';
    return 'Needs Attention';
  };

  const sortedAreas = [...lifeAreas].sort((a, b) => (b.combinedScore ?? b.score) - (a.combinedScore ?? a.score));
  const strongAreas = sortedAreas.filter(a => (a.combinedScore ?? a.score) >= 65);
  const growthAreas = sortedAreas.filter(a => (a.combinedScore ?? a.score) < 55);

  if (lifeAreas.length === 0) {
    return (
      <ReportSection title="Life Areas Analysis" icon="🎯" pageBreakBefore onNavigate={onNavigate ? () => onNavigate('calculator') : undefined} navigateLabel="View Life Areas →" navigateTab="calculator">
      <Box sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Life areas analysis will be available in the next update.
          </Typography>
        </Box>
      </ReportSection>
    );
  }

  return (
    <ReportSection title="Life Areas Analysis" icon="🎯" pageBreakBefore onNavigate={onNavigate ? () => onNavigate('calculator') : undefined} navigateLabel="View Life Areas →" navigateTab="calculator">

      {/* Score legend */}
      <Box sx={{
        mb: 3,
        p: 2,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.level1',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}>
        <Typography level="title-sm" sx={{ fontWeight: 700, color: 'primary.main' }}>
          📊 How to Read Your Scores
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.secondary', fontSize: '0.74rem', lineHeight: 1.55, mb: 0.5 }}>
          Each life area score is a <strong>weighted blend</strong> of four metrics — natal factors (Bhava Bala + Planet Impact) at <strong>65%</strong> and live temporal factors (Dasha + Transit) at <strong>35%</strong>.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ mt: '3px', width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', flexShrink: 0 }} />
            <Box>
              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>
                Bhava Bala — Structural Resilience
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.75rem' }}>
                Classical astronomical house strength. High Bhava Bala means the area has strong structural support — even during challenging dasha periods it recovers faster.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ mt: '3px', width: 8, height: 8, borderRadius: '50%', bgcolor: '#d97706', flexShrink: 0 }} />
            <Box>
              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>
                Planet Impact — Karmic Delivery Score
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.75rem' }}>
                Blended Shadbala-based score factoring in house lord strength, occupants, aspectual impact, and functional benefic/malefic nature. Positive = active support; negative = friction or lessons.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ mt: '3px', width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed', flexShrink: 0 }} />
            <Box>
              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>
                Dasha Impact — Current Period Activation
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.75rem' }}>
                0–100 measure of how strongly the active MD/AD/PD/SD lords rule, occupy, or aspect the houses governing this life area. 50 is neutral. Above 50 means the current dasha is activating this area; below 50 means the relevant houses are not a focus of the current period.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ mt: '3px', width: 8, height: 8, borderRadius: '50%', bgcolor: '#0891b2', flexShrink: 0 }} />
            <Box>
              <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>
                Transit Impact — Live Planetary Influence
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.75rem' }}>
                0–100 measure of the net benefic/malefic transit pressure on the relevant houses at the time this report was generated. 50 is perfectly neutral. Benefic transits push this above 50; malefic transits push it below.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        {lifeAreas.map((area) => {
          const icon = lifeAreaIcons[area.area.toLowerCase()] || '📋';
          const displayScore = area.combinedScore ?? area.score;
          const ratingKey = getRatingKey(displayScore);
          const theme = COLOR_THEMES[ratingKey];

          const importantRemedies = (area.remedies || []).filter(r => r.isImportant);
          const normalRemedies = (area.remedies || []).filter(r => !r.isImportant);

          // Normalize planet impact from -50..+50 → 0-100 for bar display
          const normalizedPlanetImpact = area.planetImpactScore !== undefined
            ? ((area.planetImpactScore + 50) / 100) * 100
            : null;

          // Dasha & Transit: stored 0-100 (50=neutral); display as (val-50)*2 → -100..+100 (0=neutral)
          const toSignedDisplay = (v: number) => { const d = Math.round((v - 50) * 2); return (d >= 0 ? '+' : '') + d; };
          const bars: Array<{ label: string; value: number | null; color: string; barBg: string; isNatal: boolean; isBipolar: boolean; displayLabel: string }> = [
            { label: 'Bhava Bala',     value: area.bhavaScore ?? null,        color: theme.accent, barBg: theme.barBg, isNatal: true,  isBipolar: false, displayLabel: area.bhavaScore != null ? area.bhavaScore.toFixed(1) : '—' },
            { label: 'Planet Impact',  value: normalizedPlanetImpact,         color: '#d97706',    barBg: '#fde68a',  isNatal: true,  isBipolar: true,  displayLabel: area.planetImpactScore !== undefined ? `${area.planetImpactScore >= 0 ? '+' : ''}${area.planetImpactScore.toFixed(1)}` : '—' },
            { label: 'Dasha Impact',   value: area.dashaImpactScore ?? null,  color: '#7c3aed',    barBg: '#ede9fe',  isNatal: false, isBipolar: true,  displayLabel: area.dashaImpactScore  != null ? toSignedDisplay(area.dashaImpactScore)  : '—' },
            { label: 'Transit Impact', value: area.transitImpactScore ?? null, color: '#0891b2',   barBg: '#cffafe',  isNatal: false, isBipolar: true,  displayLabel: area.transitImpactScore != null ? toSignedDisplay(area.transitImpactScore) : '—' },
          ];

          return (
            <Box
              key={area.area}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '12px',
                border: `1.5px solid ${theme.light}`,
                background: `linear-gradient(145deg, ${theme.bg} 0%, #ffffff 60%)`,
                boxShadow: `0 2px 12px ${theme.border}18`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Icon watermark */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-4px',
                  fontSize: '6rem',
                  lineHeight: 1,
                  opacity: 0.07,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  filter: 'grayscale(30%)',
                }}
              >
                {icon}
              </Box>

              {/* Coloured top accent strip */}
              <Box sx={{ height: '4px', background: `linear-gradient(90deg, ${theme.border}, ${theme.accent}88)`, borderRadius: '12px 12px 0 0' }} />

              {/* Card body */}
              <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>

                {/* Header row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{icon}</span>
                    <Typography level="title-sm" sx={{ fontWeight: 800, fontSize: '0.9rem', color: theme.text }}>
                      {area.area}
                    </Typography>
                  </Box>
                  {/* Score pill */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    background: theme.light, borderRadius: '20px',
                    px: 1, py: 0.3, border: `1px solid ${theme.border}40`,
                  }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: theme.text, lineHeight: 1 }}>
                      {displayScore}%
                    </Typography>
                    <Box sx={{ width: '1px', height: '10px', bgcolor: `${theme.border}40` }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: theme.text, lineHeight: 1 }}>
                      {getRatingLabel(displayScore)}
                    </Typography>
                  </Box>
                </Box>

                {/* 4 metric progress bars */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {bars.map(({ label, value, color, barBg, isNatal, isBipolar, displayLabel }) => {
                    if (value === null) return null;
                    const pct = Math.max(0, Math.min(100, value));
                    // Scale extremes differ: Planet = −50..+50, Dasha/Transit = −100..+100
                    const scaleMin = label === 'Planet Impact' ? '−50' : '−100';
                    const scaleMax = label === 'Planet Impact' ? '+50' : '+100';
                    return (
                      <Box key={label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.58rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {label}
                            </Typography>
                            {!isNatal && (
                              <Box sx={{ background: '#f3f4f6', borderRadius: '4px', px: 0.4, lineHeight: 1 }}>
                                <Typography sx={{ fontSize: '0.48rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>now</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: color }}>
                            {displayLabel}
                          </Typography>
                        </Box>
                        {!isBipolar ? (
                          <Box sx={{ height: '5px', borderRadius: '3px', background: barBg, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 0.3s' }} />
                          </Box>
                        ) : (
                          // Bipolar bar: center = 0, left = negative (red), right = positive (brand colour)
                          <>
                            <Box sx={{ height: '5px', borderRadius: '3px', background: '#f3f4f6', position: 'relative', overflow: 'hidden' }}>
                              <Box sx={{ position: 'absolute', left: '50%', top: 0, width: '1px', height: '100%', background: '#d1d5db', zIndex: 1 }} />
                              {Math.abs(pct - 50) > 0 && (
                                <Box sx={{
                                  position: 'absolute', top: 0, height: '100%', borderRadius: '3px',
                                  ...(pct > 50
                                    ? { left: '50%', width: `${pct - 50}%`, background: color }
                                    : { right: '50%', width: `${50 - pct}%`, background: '#dc2626' }
                                  ),
                                }} />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '2px' }}>
                              <Typography sx={{ fontSize: '0.45rem', color: '#d1d5db', lineHeight: 1 }}>{scaleMin}</Typography>
                              <Typography sx={{ fontSize: '0.45rem', color: '#d1d5db', lineHeight: 1 }}>0</Typography>
                              <Typography sx={{ fontSize: '0.45rem', color: '#d1d5db', lineHeight: 1 }}>{scaleMax}</Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Description */}
                <Typography sx={{ fontSize: '0.62rem', color: '#6b7280', lineHeight: 1.45 }}>
                  {area.description}
                </Typography>

                {/* Do's & Don'ts — only shown when remedies present */}
                {area.remedies && area.remedies.length > 0 && (
                  <Box sx={{
                    mt: 'auto',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}30`,
                    overflow: 'hidden',
                  }}>
                    {/* Section header */}
                    <Box sx={{
                      px: 1, py: 0.6,
                      background: `linear-gradient(90deg, ${theme.border}18, ${theme.accent}08)`,
                      borderBottom: `1px solid ${theme.border}25`,
                      display: 'flex', alignItems: 'center', gap: 0.5,
                    }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.text }}>
                        ✦ Do's &amp; Don'ts
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography sx={{ fontSize: '0.55rem', color: theme.text, opacity: 0.7, fontStyle: 'italic' }}>
                        {area.remedies.length} remedy{area.remedies.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>

                    {/* Important remedies (amber-highlighted rows) first */}
                    {importantRemedies.map((remedy, idx) => (
                      <Box key={`imp-${idx}`} sx={{
                        px: 1, py: 0.6,
                        background: 'linear-gradient(90deg, #fff7ed, #fffbeb)',
                        borderBottom: idx < importantRemedies.length - 1 || normalRemedies.length > 0 ? '1px solid #fed7aa40' : 'none',
                        display: 'flex', gap: 0.75, alignItems: 'flex-start',
                      }}>
                        <Box sx={{ width: '14px', height: '14px', borderRadius: '50%', background: '#f97316', flexShrink: 0, mt: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ fontSize: '0.5rem', color: '#fff', lineHeight: 1, fontWeight: 800 }}>!</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mb: '2px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.65rem', lineHeight: 1.3, color: '#c2410c' }}>
                              {remedy.title}
                            </Typography>
                            <Box sx={{ background: '#fed7aa', borderRadius: '4px', px: 0.5, py: 0.1, lineHeight: 1, flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.55rem', color: '#9a3412', fontStyle: 'italic', opacity: 0.75 }}>
                              {remedy.planet}
                            </Typography>
                          </Box>
                          {remedy.description && (
                            <Typography sx={{ fontSize: '0.6rem', color: '#92400e', lineHeight: 1.35 }}>
                              {remedy.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}

                    {/* Normal remedies */}
                    {normalRemedies.map((remedy, idx) => (
                      <Box key={`norm-${idx}`} sx={{
                        px: 1, py: 0.6,
                        background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                        borderBottom: idx < normalRemedies.length - 1 ? `1px solid ${theme.border}15` : 'none',
                        display: 'flex', gap: 0.75, alignItems: 'flex-start',
                      }}>
                        <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: theme.accent, flexShrink: 0, mt: '5px' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mb: '2px' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.3, color: '#1f2937' }}>
                              {remedy.title}
                            </Typography>
                            <Typography sx={{ fontSize: '0.55rem', color: '#6b7280', fontStyle: 'italic' }}>
                              {remedy.planet}
                            </Typography>
                          </Box>
                          {remedy.description && (
                            <Typography sx={{ fontSize: '0.6rem', color: '#6b7280', lineHeight: 1.35 }}>
                              {remedy.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Strengths + Growth summary side by side */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, mt: 2 }}>
        {/* Strongest Areas */}
        <Box sx={{
          borderRadius: '10px', overflow: 'hidden',
          border: '1.5px solid #bbf7d0',
          background: 'linear-gradient(135deg, #f0fdf4, #ffffff)',
          boxShadow: '0 2px 8px #16a34a14',
        }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #16a34a1a, #16a34a06)', borderBottom: '1px solid #bbf7d0' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#15803d' }}>✨ Strongest Areas</Typography>
          </Box>
          <Box sx={{ p: 1.25 }}>
            {strongAreas.slice(0, 3).map((area, i) => (
              <Box key={area.area} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: i < 2 ? 0.5 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', width: '14px' }}>{i + 1}.</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1f2937' }}>
                    {lifeAreaIcons[area.area.toLowerCase()] || ''} {area.area}
                  </Typography>
                </Box>
                <Box sx={{ background: '#dcfce7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d' }}>
                    {area.combinedScore !== undefined ? `${area.combinedScore}%` : '—'}
                  </Typography>
                </Box>
              </Box>
            ))}
            {strongAreas.length === 0 && (
              <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontStyle: 'italic' }}>Chart analysis pending</Typography>
            )}
          </Box>
        </Box>

        {/* Areas for Growth */}
        <Box sx={{
          borderRadius: '10px', overflow: 'hidden',
          border: '1.5px solid #fde68a',
          background: 'linear-gradient(135deg, #fffbeb, #ffffff)',
          boxShadow: '0 2px 8px #d9770614',
        }}>
          <Box sx={{ px: 1.5, py: 0.75, background: 'linear-gradient(90deg, #d977061a, #d9770606)', borderBottom: '1px solid #fde68a' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#b45309' }}>🎯 Areas for Growth</Typography>
          </Box>
          <Box sx={{ p: 1.25 }}>
            {growthAreas.slice(0, 3).map((area, i) => (
              <Box key={area.area} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: i < 2 ? 0.5 : 0 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1f2937' }}>
                  {lifeAreaIcons[area.area.toLowerCase()] || ''} {area.area}
                </Typography>
                <Box sx={{ background: '#fef3c7', borderRadius: '20px', px: 0.75, py: 0.2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309' }}>
                    {area.combinedScore !== undefined ? `${area.combinedScore}%` : '—'}
                  </Typography>
                </Box>
              </Box>
            ))}
            {growthAreas.length === 0 && (
              <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontStyle: 'italic' }}>All areas show strength</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </ReportSection>
  );
};